/**
 * Breadcrumb Mixin
 *
 * A functional mixin that adds breadcrumb tracking capabilities to third-party Stimulus controllers.
 * Uses the class wrapper pattern to extend controllers from npm packages without forking them.
 *
 * This pattern allows us to add the `addBreadcrumb()` method to controllers that extend
 * third-party libraries like @stimulus-components/dropdown, stimulus-autocomplete, etc.
 *
 * @module mixins/breadcrumb
 *
 * @example
 * // Extending a third-party controller with breadcrumb tracking
 * import Dropdown from '@stimulus-components/dropdown'
 * import { withBreadcrumb } from '../mixins/breadcrumb'
 *
 * export default class extends withBreadcrumb(Dropdown) {
 *   toggle(event) {
 *     super.toggle(event)
 *     this.addBreadcrumb('UI', 'dropdown_toggle', {
 *       open: this.element.getAttribute('aria-expanded') === 'true'
 *     })
 *   }
 * }
 *
 * @example
 * // Extending Autocomplete with breadcrumb tracking
 * import { Autocomplete } from 'stimulus-autocomplete'
 * import { withBreadcrumb } from '../mixins/breadcrumb'
 *
 * export default class extends withBreadcrumb(Autocomplete) {
 *   onSelect(event) {
 *     this.addBreadcrumb('UI', 'autocomplete_select', {
 *       value: event.target.textContent
 *     })
 *   }
 * }
 */

import appsignal from '../application/appsignal'

/**
 * Creates a class that extends the provided base class with breadcrumb tracking.
 *
 * This functional mixin wraps a base class and adds the `addBreadcrumb()` method,
 * allowing you to track user actions in any Stimulus controller.
 *
 * @param {import('@hotwired/stimulus').Controller} BaseClass - The controller class to extend
 * @returns {import('@hotwired/stimulus').Controller} Extended class with addBreadcrumb() method
 *
 * @example
 * // With third-party controller
 * import Reveal from '@stimulus-components/reveal'
 * import { withBreadcrumb } from '../mixins/breadcrumb'
 *
 * export default class extends withBreadcrumb(Reveal) {
 *   connect() {
 *     super.connect()
 *     this.addBreadcrumb('UI', 'histogram_connected')
 *   }
 * }
 *
 * @example
 * // With base Stimulus Controller
 * import { Controller } from '@hotwired/stimulus'
 * import { withBreadcrumb } from '../mixins/breadcrumb'
 *
 * export default class extends withBreadcrumb(Controller) {
 *   handleClick() {
 *     this.addBreadcrumb('UI', 'button_click')
 *   }
 * }
 */
export const withBreadcrumb = (BaseClass) =>
  class extends BaseClass {
    /**
     * Adds a breadcrumb to track user actions for debugging context.
     *
     * Breadcrumbs are automatically sent to AppSignal when errors occur,
     * providing valuable context about what the user was doing before the error.
     *
     * The controller identifier is automatically included in metadata.
     * Network requests are automatically tracked by @appsignal/plugin-breadcrumbs-network.
     *
     * @param {string} category - The category of the action (e.g., 'UI', 'navigation', 'form')
     * @param {string} action - The specific action being tracked (e.g., 'click', 'submit', 'open')
     * @param {Object} [metadata={}] - Additional context about the action
     *
     * @example
     * this.addBreadcrumb('UI', 'dropdown_toggle', {
     *   open: true,
     *   itemCount: 5
     * })
     */
    addBreadcrumb(category, action, metadata = {}) {
      appsignal.addBreadcrumb({
        category,
        action,
        metadata: {
          controller: this.identifier,
          ...metadata,
        },
      })
    }
  }
